import { jsx as _jsx } from "react/jsx-runtime";
/**
 * The React side of the text layer (feature 0050): `useT()`, for the components that have no render context.
 *
 * ── Why this exists next to RenderProvider rather than inside it ───────────────────────────────────────
 *
 * Eleven components render shopper-facing sentences and receive NO context at all — `UpsellOfferCard`
 * ("Offre spéciale", "Oui, ajouter à ma commande", the quantity `aria-label`s) and
 * `PostPurchaseUpsellChain` among them. They are leaves, mounted from several places, and threading a
 * context through every caller to reach them would be a refactor of the callers rather than of the text.
 *
 * So the texts travel on their own React context. `PageRenderer` mounts it, and so does every island — the
 * islands especially, because they hydrate their own roots and never see the server's provider tree.
 *
 * ── Why useT never throws ──────────────────────────────────────────────────────────────────────────────
 *
 * `useRenderContext()` throws when there is no provider, which is right for it: a section without data is a
 * wiring bug worth stopping on. `useT()` does the opposite and falls back to the bundled dictionary,
 * because a missing provider must not cost a shop its buy box. There is no error boundary above an island
 * root (`hydrateRoot`, bare), so a throw here does not surface an error — it unmounts the root and the
 * component disappears from the page with nothing in the console. That failure has happened here before.
 */
import { createContext, useContext, useMemo } from 'react';
import { FALLBACK_LOCALE, t } from '@aquilixshop/i18n';
const Ctx = createContext(null);
/**
 * Publish the page's language and dictionary to everything below.
 *
 * Mounted by `PageRenderer` for the server-rendered tree, and by each island for its own root. Mounting it
 * twice is harmless — the nearest one wins, which is what an island inside a page should do.
 */
export function TextsProvider({ locale, texts, children, }) {
    const value = useMemo(() => ({ locale, texts }), [locale, texts]);
    return _jsx(Ctx.Provider, { value: value, children: children });
}
/**
 * The translator for a component that has no render context.
 *
 * Returns a function rather than a string so a component can translate several keys, and so the identity is
 * stable across renders — passing it into a `useMemo` dependency list should not re-run the memo on every
 * paint.
 */
export function useT() {
    const value = useContext(Ctx);
    const locale = value?.locale ?? FALLBACK_LOCALE;
    const texts = value?.texts;
    return useMemo(() => {
        const context = { locale, texts };
        return (key, params) => t(context, key, params);
    }, [locale, texts]);
}
/** The page's language, for the rare component that needs to branch on it rather than translate. */
export function useLocale() {
    return useContext(Ctx)?.locale ?? FALLBACK_LOCALE;
}
//# sourceMappingURL=TextsProvider.js.map